// Componentes de un kit: SKUs existentes de la cadena y cuántas piezas lleva cada kit.
import { useId } from 'react';
import { Controller, useFieldArray, type Control } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { ProductStatusEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductStatusEnum';
import { Button, NumberInput } from '@/kit';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import { productLabel } from '@/api/services/retail-catalog/backoffice';
import { itemTypeOf } from '../../lib/catalogForms';
import { FIELD_CLASS, SkuCombobox, type SkuOption } from './SkuCombobox';

export interface KitComponentValues {
    components: { sku: string; qty: number }[];
}

/** SKUs que pueden ir en un kit (activos y que no son kit) más los que ya tiene, en orden alfabético. */
export function componentOptions(
    products: readonly CatalogProduct[],
    excludeSku?: string,
    keepSkus: readonly string[] = [],
): SkuOption[] {
    const eligible = (p: CatalogProduct) =>
        p.status === ProductStatusEnum.ACTIVE && itemTypeOf(p) !== 'KIT' && p.sku !== excludeSku;
    return products
        .filter((p) => eligible(p) || keepSkus.includes(p.sku))
        .map((p) => ({
            value: p.sku,
            label: `${productLabel(p) || p.description || p.sku} · ${p.sku}`,
            keywords: [p.sku, p.brand, p.model, p.description].filter(Boolean).join(' '),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export function KitComponentsField({
    control,
    options,
    isLoading,
    error,
}: {
    control: Control<KitComponentValues>;
    options: SkuOption[];
    isLoading: boolean;
    error?: string;
}) {
    const { fields, append, remove } = useFieldArray({ control, name: 'components' });
    return (
        <div className="flex flex-col gap-2">
            {fields.length === 0 ? (
                <p className="text-[11px] italic text-muted-foreground">
                    Sin componentes. Agrega los SKUs que forman el kit.
                </p>
            ) : null}
            {fields.map((item, index) => (
                <div key={item.id} className="grid grid-cols-[1fr_96px_32px] items-start gap-2">
                    <Controller
                        control={control}
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- ruta tipada de react-hook-form
                        name={`components.${index}.sku`}
                        render={({ field, fieldState }) => (
                            <SkuCombobox
                                label={`Componente ${String(index + 1)}`}
                                value={field.value}
                                onChange={field.onChange}
                                options={options}
                                placeholder={
                                    isLoading
                                        ? 'Cargando productos…'
                                        : 'Busca por SKU, marca o modelo'
                                }
                                error={fieldState.error?.message}
                                disabled={isLoading}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- ruta tipada de react-hook-form
                        name={`components.${index}.qty`}
                        render={({ field, fieldState }) => (
                            <QuantityField
                                index={index}
                                value={field.value}
                                onChange={field.onChange}
                                error={fieldState.error?.message}
                            />
                        )}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            remove(index);
                        }}
                        aria-label={`Quitar el componente ${String(index + 1)}`}
                        // Mismo alto que la etiqueta (20 px + 6 px de aire): queda a la par del campo.
                        className="mt-[26px] flex size-9 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-danger"
                    >
                        <Trash2 className="size-3.5" aria-hidden />
                    </button>
                </div>
            ))}
            <div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="size-3.5" aria-hidden />}
                    onClick={() => {
                        append({ sku: '', qty: 1 });
                    }}
                >
                    Agregar componente
                </Button>
            </div>
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
    );
}

/** «Cantidad» con la misma etiqueta y el mismo alto que el selector de SKU de su renglón. */
function QuantityField({
    index,
    value,
    onChange,
    error,
}: {
    index: number;
    value: number;
    onChange: (value: number) => void;
    error?: string;
}) {
    const id = useId();
    return (
        <div className="space-y-1.5">
            <label htmlFor={id} className="block text-sm font-medium text-foreground">
                Cantidad
            </label>
            <NumberInput
                id={id}
                aria-label={`Piezas del componente ${String(index + 1)}`}
                value={value}
                onValueChange={onChange}
                min={1}
                step={1}
                className={FIELD_CLASS}
            />
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
    );
}
